import React, { useState, useEffect } from 'react';
import { API_URL } from '../utils/constants';

const HookGenerator = ({ session, projectId, content, onHookSelected, onClose }) => {
  const [hooks, setHooks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedIndex, setSelectedIndex] = useState(null);

  useEffect(() => {
    generateHooks();
  }, []); // eslint-disable-line

  const generateHooks = async () => {
    if (!session || !content) return;
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`${API_URL}/api/generate-hooks`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${session.access_token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ projectId, content })
      });

      if (res.ok) {
        const data = await res.json();
        setHooks(data.hooks || []);
      } else {
        const data = await res.json().catch(() => ({}));
        setError(data.error || 'Failed to generate hooks');
      }
    } catch (err) {
      console.error('Failed to generate hooks:', err);
      setError('Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleConfirm = () => {
    if (selectedIndex !== null) {
      onHookSelected(hooks[selectedIndex]);
      onClose();
    }
  };

  return (
    <div style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex',
      alignItems: 'center', justifyContent: 'center', zIndex: 1000, backdropFilter: 'blur(8px)',
      animation: 'fadeIn 0.2s ease', padding: '20px'
    }} onClick={onClose}>
      <div style={{
        background: 'var(--bg-card)', borderRadius: '20px', padding: '32px', width: '100%',
        maxWidth: '700px', boxShadow: 'var(--shadow-lg)', border: '1px solid var(--border-color)',
        animation: 'modalIn 0.3s cubic-bezier(0.16, 1, 0.3, 1)', maxHeight: '90vh', overflow: 'auto'
      }} onClick={e => e.stopPropagation()}>

        <div style={{ textAlign: 'center', marginBottom: '32px' }}>
          <div style={{ fontSize: '40px', marginBottom: '12px' }}>🎯</div>
          <h2 style={{ fontSize: '24px', fontWeight: '700', color: 'var(--text-primary)', marginBottom: '8px' }}>
            Choose Your Hook
          </h2>
          <p style={{ color: 'var(--text-secondary)', fontSize: '14px' }}>
            The first tweet drives your thread's performance. Pick the hook that resonates best.
          </p>
        </div>

        {loading ? (
          <div style={{ textAlign: 'center', padding: '40px 20px' }}>
            <div style={{
              width: '40px', height: '40px', border: '3px solid var(--border-color)',
              borderTopColor: 'var(--accent-primary)', borderRadius: '50%',
              animation: 'spin 1s linear infinite', margin: '0 auto 16px'
            }} />
            <p style={{ color: 'var(--text-secondary)' }}>Generating viral hooks...</p>
          </div>
        ) : error ? (
          <div style={{ textAlign: 'center', padding: '40px 20px' }}>
            <div style={{ fontSize: '48px', marginBottom: '16px' }}>😕</div>
            <p style={{ color: 'var(--error)', fontSize: '15px', marginBottom: '16px' }}>{error}</p>
            <button
              onClick={generateHooks}
              style={{
                padding: '10px 20px', borderRadius: '10px', border: 'none',
                background: 'var(--accent-gradient)', color: 'white',
                fontSize: '14px', fontWeight: '600', cursor: 'pointer', fontFamily: 'inherit'
              }}
            >
              Try Again
            </button>
          </div>
        ) : hooks.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '40px 20px' }}>
            <p style={{ color: 'var(--text-secondary)' }}>No hooks were generated. Try again with different content.</p>
          </div>
        ) : (
          <>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', marginBottom: '24px' }}>
              {hooks.map((hook, index) => (
                <button
                  key={index}
                  onClick={() => setSelectedIndex(index)}
                  style={{
                    padding: '20px', borderRadius: '12px', textAlign: 'left',
                    border: `2px solid ${selectedIndex === index ? 'var(--accent-primary)' : 'var(--border-color)'}`,
                    background: selectedIndex === index ? 'var(--accent-subtle-bg)' : 'var(--bg-secondary)',
                    cursor: 'pointer', fontFamily: 'inherit', transition: 'all 0.2s',
                    position: 'relative'
                  }}
                  onMouseEnter={e => {
                    if (selectedIndex !== index) e.currentTarget.style.borderColor = 'var(--accent-primary)';
                  }}
                  onMouseLeave={e => {
                    if (selectedIndex !== index) e.currentTarget.style.borderColor = 'var(--border-color)';
                  }}
                >
                  {selectedIndex === index && (
                    <div style={{
                      position: 'absolute', top: '12px', right: '12px',
                      width: '24px', height: '24px', borderRadius: '50%',
                      background: 'var(--accent-primary)', display: 'flex',
                      alignItems: 'center', justifyContent: 'center', color: 'white',
                      fontSize: '14px', fontWeight: '700'
                    }}>✓</div>
                  )}

                  <div style={{
                    fontSize: '11px', fontWeight: '600', color: 'var(--accent-primary)',
                    textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '8px'
                  }}>
                    {hook.style}
                  </div>

                  <div style={{
                    fontSize: '15px', lineHeight: '1.6', color: 'var(--text-primary)',
                    fontWeight: '500', marginBottom: '8px'
                  }}>
                    {hook.text}
                  </div>

                  <div style={{
                    fontSize: '12px', color: hook.text.length > 280 ? 'var(--error)' : 'var(--text-muted)',
                    display: 'flex', alignItems: 'center', gap: '8px'
                  }}>
                    <span>{hook.text.length}/280</span>
                    {hook.text.length > 280 && <span>⚠️ Too long</span>}
                  </div>
                </button>
              ))}
            </div>

            <div style={{ display: 'flex', gap: '12px' }}>
              <button
                onClick={onClose}
                style={{
                  flex: 1, padding: '14px', borderRadius: '10px',
                  border: '2px solid var(--border-color)', background: 'transparent',
                  color: 'var(--text-secondary)', fontSize: '14px', cursor: 'pointer', fontFamily: 'inherit'
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleConfirm}
                disabled={selectedIndex === null}
                style={{
                  flex: 1, padding: '14px', borderRadius: '10px', border: 'none',
                  background: 'var(--accent-gradient)', color: 'white',
                  fontSize: '14px', fontWeight: '600', cursor: 'pointer', fontFamily: 'inherit',
                  opacity: selectedIndex === null ? 0.5 : 1
                }}
              >
                Use This Hook
              </button>
            </div>

            <div style={{
              marginTop: '20px', padding: '16px', borderRadius: '10px',
              background: 'var(--accent-subtle-bg)', border: '1px solid var(--accent-subtle-border)'
            }}>
              <p style={{ fontSize: '13px', color: 'var(--text-secondary)', lineHeight: '1.5' }}>
                💡 <strong style={{ color: 'var(--text-primary)' }}>Pro tip:</strong> Test different hook styles to see what resonates with your audience. Track performance in Analytics to optimize over time.
              </p>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default HookGenerator;
